import {
  APIWorkspaceFolder,
  ArdoqId,
  AssetFolder,
  AssetType,
  ContentItem,
  ContentByType,
  ResourceType,
} from '@ardoq/api-types';
import { groupBy, mapValues } from 'lodash';
import { findNestedContent } from './utils';
import {
  persistentReducedStream,
  reducer,
  streamReducer,
} from '@ardoq/rxbeach';
import { filterResourceType, websocket$ } from 'sync/websocket$';
import { fetchAllReducer, websocketReducer } from 'streams/crud/reducers';
import { removeEntityById } from '@ardoq/common-helpers';
import { moveAssetsSuccess } from './actions';

export type AssetFoldersState = AssetFolder[];

const groupByType = (content: ContentItem[]): ContentByType => ({
  [AssetType.FOLDER]: [],
  [AssetType.METAMODEL]: [],
  [AssetType.PRESENTATION]: [],
  [AssetType.SCENARIO]: [],
  [AssetType.SURVEY]: [],
  [AssetType.WORKSPACE]: [],
  [AssetType.REPORT]: [],
  [AssetType.DASHBOARD]: [],
  [AssetType.TRAVERSAL]: [],
  [AssetType.BOOKMARK]: [],
  [AssetType.VIEWPOINT]: [],
  [AssetType.BROADCAST]: [],
  [AssetType.CUSTOM_AGENT]: [],
  ...mapValues(groupBy(content, 'type'), value => value.map(({ _id }) => _id)),
});

const toAssetFolder = (
  folder: APIWorkspaceFolder,
  nestedContentHash: Record<ArdoqId, ContentItem[]>
): AssetFolder => ({
  ...folder,
  contentByType: groupByType(folder.content),
  nestedContent: nestedContentHash[folder._id] || [],
  nestedContentByType: groupByType(nestedContentHash[folder._id] || []),
  meta: {},
});

export const resetFolders = (
  _: AssetFoldersState,
  folders: APIWorkspaceFolder[]
): AssetFoldersState => {
  const nestedContentHash = findNestedContent(folders);
  return folders.map(folder => toAssetFolder(folder, nestedContentHash));
};

const updateFolder = (
  state: AssetFoldersState,
  folder: APIWorkspaceFolder
): AssetFoldersState => {
  const newModels = state.map(existingFolder =>
    existingFolder._id === folder._id ? folder : existingFolder
  );

  return resetFolders(state, newModels);
};

export const updateFolders = (
  state: AssetFoldersState,
  folders: APIWorkspaceFolder[]
): AssetFoldersState => {
  const updatedById = new Map(folders.map(folder => [folder._id, folder]));
  const knownIds = new Set(state.map(({ _id }) => _id));
  return resetFolders(state, [
    ...state.map(
      existingFolder => updatedById.get(existingFolder._id) ?? existingFolder
    ),
    ...folders.filter(({ _id }) => !knownIds.has(_id)),
  ]);
};

export const createFolder = (
  state: AssetFoldersState,
  folder: APIWorkspaceFolder
): AssetFoldersState =>
  resetFolders(state, [
    folder,
    ...state.filter(({ _id }) => _id !== folder._id),
  ]);

export const assetFolders$ = persistentReducedStream<AssetFoldersState>(
  'assetFolder$',
  [],
  [
    fetchAllReducer(resetFolders),
    reducer(moveAssetsSuccess, updateFolders),
    streamReducer(
      websocket$.pipe(
        filterResourceType<APIWorkspaceFolder>(ResourceType.WORKSPACEFOLDER)
      ),
      websocketReducer({
        create: createFolder,
        update: updateFolder,
        delete: removeEntityById,
      })
    ),
  ],
  { namespace: 'workspacefolder' }
);
